// HTTP client for our API (apps/api). Same origin, so the browser sends our HttpOnly session cookie
// by itself (M4, ADR-0007): no token ever lives in JavaScript. The workspace in the URL is only a
// request: the API verifies membership on every call.
import { HttpError } from "ra-core";

const API_BASE =
  (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "") + "/api";
const WORKSPACE_KEY = "crm.workspace";

export type Membership = {
  workspace: { id: string; name: string; slug: string };
  role: "owner" | "admin" | "member" | "viewer";
  title: string | null;
};
export type Me = {
  user: {
    id: string;
    email: string;
    name: string;
    given_name: string | null;
    family_name: string | null;
    avatar: { src?: string } | null;
  };
  memberships: Membership[];
  /** Whether this user may create a workspace now (first run, or open creation). */
  can_create_workspace: boolean;
};

export async function api<T>(
  path: string,
  init: {
    method?: string;
    body?: unknown;
    query?: Record<string, string | undefined>;
  } = {},
): Promise<T> {
  const url = new URL(API_BASE + path, window.location.origin);
  for (const [key, value] of Object.entries(init.query ?? {}))
    if (value !== undefined) url.searchParams.set(key, value);
  const headers: Record<string, string> = { accept: "application/json" };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  const res = await fetch(url, {
    method: init.method ?? "GET",
    headers,
    credentials: "same-origin",
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = (payload as { error?: { code?: string; message?: string } })
      .error;
    throw new HttpError(error?.message ?? res.statusText, res.status, {
      ...payload,
      code: error?.code,
      details: (payload as { error?: { details?: unknown } }).error?.details,
    });
  }
  return payload as T;
}

// ── Session: who am I, which workspace ─────────────────────────────────────────────────────────
let mePromise: Promise<Me> | null = null;

export function getMe(): Promise<Me> {
  mePromise ??= api<Me>("/me").catch((error) => {
    mePromise = null;
    throw error;
  });
  return mePromise;
}

export function clearSession(): void {
  mePromise = null;
}

/**
 * Forget everything cached about the previous user or workspace: who I am, and the mobile app's
 * persisted query cache (it lives in localStorage), so no data outlives a sign-out or a switch.
 */
export function clearClientCaches(): void {
  clearSession();
  try {
    window.localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
  } catch {
    // Storage unavailable: nothing persisted either.
  }
}

/**
 * Off to the provider via our API; we come back to `returnTo` (a same-origin path such as "/" or
 * "/#/invite"). Never pass a URL holding a secret: the return path is stored server-side.
 */
export function signIn(returnTo = "/"): void {
  window.location.assign(
    `${API_BASE}/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
  );
}

/** Ends our session; returns where to go next (the provider's logout page, or "/"). */
export async function signOut(): Promise<string> {
  clearClientCaches();
  const { redirect } = await api<{ redirect: string }>("/auth/logout", {
    method: "POST",
  }).catch(() => ({ redirect: "/" }));
  return redirect;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The active membership: the remembered workspace if still a member, else the first one. */
export async function currentMembership(): Promise<Membership | undefined> {
  const { memberships } = await getMe();
  const remembered = storage()?.getItem(WORKSPACE_KEY);
  return (
    memberships.find((m) => m.workspace.id === remembered) ?? memberships[0]
  );
}

export function rememberWorkspace(id: string): void {
  storage()?.setItem(WORKSPACE_KEY, id);
  clearClientCaches();
}

export async function workspacePath(path: string): Promise<string> {
  const membership = await currentMembership();
  if (!membership)
    throw new HttpError("You are not a member of any workspace yet.", 403, {
      code: "FORBIDDEN",
    });
  return `/workspaces/${membership.workspace.id}${path}`;
}
